/* eslint-disable react-refresh/only-export-components */
import * as React from "react";
import { BadgeCheck, CircleDashed } from "lucide-react";
import { Badge } from "../badge";

type Tier = 1 | 2 | 3 | 4;

const tierIconSizeMap = {
  small: 12,
  medium: 13,
  large: 14,
};

/**
 * Master §6 replaced Tier 1–4 with one status: Unverified or Verified (proof of funds + articles
 * reviewed). Proven and Trusted come from ratings later. Old tier 3 and 4 values read as Verified.
 */
const tierNames: Record<Tier, string> = {
  1: "Unverified",
  2: "Verified",
  3: "Verified",
  4: "Verified",
};

/**
 * One source of truth for status vocabulary. Network cards, Contacts, the
 * Verification Center and the profile all read from here so the
 * same member is never called two different things on two screens.
 */
const tierDescriptions: Record<Tier, string> = {
  1: "No verified entity yet. Can browse listings without full addresses and can't make offers.",
  2: "Proof of funds and articles reviewed. Sees full addresses and can make offers.",
  3: "Proof of funds and articles reviewed. Sees full addresses and can make offers.",
  4: "Proof of funds and articles reviewed. Sees full addresses and can make offers.",
};

/** What each status unlocks. */
const tierBenefits: Record<Tier, string[]> = {
  1: ["Browse listings", "Create buy boxes"],
  2: ["Full addresses and maps", "Make offers under your verified entities", "Verified badge"],
  3: [],
  4: [],
};

/** What verification means for a seller: the badge buyers see next to your name. */
const sellerTierBenefits: Record<Tier, string[]> = {
  1: ["List properties and receive offers"],
  2: ["Verified badge on your listings, offers and deals"],
  3: [],
  4: [],
};

const TIERS: readonly Tier[] = [1, 2];

export interface TierBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  tier: 1 | 2 | 3 | 4;
  size?: "small" | "medium" | "large";
  showLabel?: boolean;
  showTooltip?: boolean;
}

/** API data can carry 0, null or an out-of-range tier; never render a blank pill */
const normalizeTier = (tier: unknown): Tier => (Number(tier) >= 2 ? 2 : 1);

function TierIcon({ tier, size }: { tier: Tier; size: number }) {
  const iconProps = { size, strokeWidth: 2.25, className: "shrink-0" };

  switch (tier) {
    case 2:
    case 3:
    case 4:
      return <BadgeCheck {...iconProps} />;
    case 1:
    default:
      return <CircleDashed {...iconProps} />;
  }
}

/**
 * Verification pill: a neutral chip where the icon carries the status
 * (dashed circle = unverified, check = verified). Kept neutral so it never competes
 * with status pills or the one brand accent.
 */
function TierBadge({
  tier: rawTier = 1,
  size = "medium",
  showLabel = true,
  showTooltip = true,
  className,
  ...props
}: TierBadgeProps) {
  const tier = normalizeTier(rawTier);
  const iconSize = tierIconSizeMap[size || "medium"];
  const tierName = tierNames[tier];
  const tierDescription = tierDescriptions[tier];

  return (
    <Badge
      variant="default"
      size={size}
      icon={<TierIcon tier={tier} size={iconSize} />}
      label={showLabel ? tierName : undefined}
      showTooltip={showTooltip}
      tooltipText={`${tierName}: ${tierDescription}`}
      className={className}
      aria-label={tierName}
      {...props}
    />
  );
}

// Re-export for backward compatibility
const tierBadgeVariants = {
  1: "tier1",
  2: "tier2",
  3: "tier3",
  4: "tier4",
} as const;

export {
  TierBadge,
  TierIcon,
  tierBadgeVariants,
  tierNames,
  tierDescriptions,
  tierBenefits,
  sellerTierBenefits,
  normalizeTier,
  TIERS,
};
export type { Tier };
